// src/Components/AIHelpAssistant.js
import React, { useState, useRef, useEffect } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  IconButton,
  Avatar,
  CircularProgress,
  Fab,
  Zoom,
  Slide,
  Button,
  Chip,
  Tooltip,
  InputAdornment
} from '@mui/material';
import {
  SmartToy as AIIcon,
  Send as SendIcon,
  Close as CloseIcon,
  Refresh as RefreshIcon,
  Person as PersonIcon,
  QuestionMark as QuestionIcon
} from '@mui/icons-material';
import { useProfile } from '../contexts/ProfileContext';
import EmojiPicker from './EmojiPicker';

// Sample suggested questions
const SUGGESTED_QUESTIONS = [
  "How do I add a family member?",
  "How do I upload photos?",
  "Where can I find the family tree?",
  "How do I create an event?",
  "How do I update my profile?",
  "What is the Family Directory?"
];

// AI responses based on keywords (local fallback when no API is configured)
const AI_RESPONSES = {
  'family member': {
    answer: "To add a family member, go to your **Profile** page and click the **\"Send Invite\"** button in the Family Management section. Enter their email address and select their relationship to you. They'll receive an invitation to join your family board.",
    followUp: ["How do I remove a family member?", "Can I change someone's role?"]
  },
  'add member': {
    answer: "To add a family member, go to your **Profile** page and click the **\"Send Invite\"** button in the Family Management section. Enter their email address and select their relationship to you. They'll receive an invitation to join your family board.",
    followUp: ["How do I remove a family member?", "Can I change someone's role?"]
  },
  'upload photo': {
    answer: "To upload photos:\n1. Go to **Photos** from the navigation menu\n2. Select the **Family Photos** tab\n3. Click the **Upload** button\n4. Select your photos and they'll be added to your family's shared album.\n\nYou can also upload to the secure **Photo Vault** if you're an admin.",
    followUp: ["How do I organize photos into albums?", "Who can see my photos?"]
  },
  'photo': {
    answer: "The **Photos** section lets you share and organize family photos. Navigate to **Photos** from the Family menu to:\n- View and upload family photos\n- Access shared documents\n- Use the secure Family Vault (admins only)\n\nPhotos are shared with all family members by default.",
    followUp: ["How do I upload photos?", "How do I create an album?"]
  },
  'family tree': {
    answer: "To view or edit the family tree:\n1. Go to **Family** → **Family Tree** from the navigation\n2. You'll see an interactive tree showing all family relationships\n3. Click on any member to see their details\n4. Admins can add or edit relationships\n\nThe tree automatically updates as new members join.",
    followUp: ["How do I add relationships?", "Can I print the family tree?"]
  },
  'tree': {
    answer: "The **Family Tree** is an interactive visualization of your family connections. Access it from **Family** → **Family Tree** in the navigation menu. You can:\n- View all family members and their relationships\n- Click on members to see their profiles\n- Zoom and pan to explore larger families",
    followUp: ["How do I add someone to the tree?", "How do relationships work?"]
  },
  'create event': {
    answer: "To create a family event:\n1. Go to **Family** → **Events**\n2. Click the **Add Event** button\n3. Fill in the event details (title, date, location, etc.)\n4. Choose the event type (casual, reunion, birthday, etc.)\n5. Click **Save** to create the event\n\nFamily members will be notified about new events.",
    followUp: ["How do I set event reminders?", "Can I invite specific family members?"]
  },
  'event': {
    answer: "The **Events** feature helps you plan and track family gatherings. You can create different types of events:\n- **Casual** - Regular get-togethers\n- **Reunion** - Large family reunions with detailed planning\n- **Birthday** - Birthday celebrations\n- **Holiday** - Holiday gatherings\n\nGo to **Family** → **Events** to get started.",
    followUp: ["How do I create an event?", "How do I RSVP to an event?"]
  },
  'profile': {
    answer: "To update your profile:\n1. Click on your avatar in the top right corner\n2. Select **Profile** from the menu\n3. Edit your personal information, contact details, and bio\n4. Click **Update Profile** to save changes\n\nYou can also add your **Professional Profile** to appear in the Family Directory!",
    followUp: ["What is the Professional Profile?", "How do I change my profile picture?"]
  },
  'directory': {
    answer: "The **Family Directory** is a business and professional services directory for your family. You can:\n- Browse family members' professional profiles\n- Search by skill, industry, or service\n- Find services within your family (accounting, legal, handyman, etc.)\n\nAccess it from **Family** → **Family Directory**. Add your own services in your Profile → Professional Profile tab.",
    followUp: ["How do I add my services?", "Can I contact family members through the directory?"]
  },
  'professional': {
    answer: "Your **Professional Profile** lets you share your work experience and services with family:\n1. Go to **Profile**\n2. Click the **Professional Profile** tab\n3. Add your profession, skills, certifications, and services\n4. Toggle **\"Show in Directory\"** to appear in the Family Directory\n\nFamily members can then find and contact you for services!",
    followUp: ["What is the Family Directory?", "Can I list my business?"]
  },
  'document': {
    answer: "To manage documents:\n1. Go to **Media** → **Documents**\n2. You can view, upload, and organize family documents\n3. The **Family Vault** (admin access) provides secure storage for sensitive documents\n\nDocuments are shared with family members based on permissions.",
    followUp: ["How do I upload a document?", "What is the Family Vault?"]
  },
  'vault': {
    answer: "The **Family Vault** is a secure storage area for sensitive documents. Only admins can access and manage the vault. To use it:\n1. Go to **Photos** or **Documents**\n2. Navigate to the **Family Vault** tab\n3. Upload important documents that need extra security\n\nVault documents have restricted access for privacy.",
    followUp: ["Who can access the vault?", "How do I become an admin?"]
  },
  'announcement': {
    answer: "To make family announcements:\n1. Go to **Family Board**\n2. Find the **Announcements** section\n3. Click **Create Announcement**\n4. Write your message and set the priority level\n5. Family members will see it on the board and may receive notifications\n\nUse high priority for urgent news!",
    followUp: ["How do notifications work?", "Can I pin an announcement?"]
  },
  'help': {
    answer: "I'm here to help you navigate FamilyForLife! Here are some things I can help with:\n- **Adding family members** and managing relationships\n- **Uploading photos** and documents\n- **Creating events** and announcements\n- **Using the Family Tree**\n- **Setting up your profile**\n- **Finding family services** in the Directory\n\nWhat would you like to know more about?",
    followUp: ["How do I add a family member?", "How do I upload photos?"]
  },
  'default': {
    answer: "I'm not sure about that specific question, but I'm happy to help! Here are some common topics I can assist with:\n- Adding and managing family members\n- Uploading photos and documents\n- Creating events and announcements\n- Using the Family Tree\n- Finding family services in the Directory\n\nYou can also create a **support ticket** in the Help Center for personalized assistance from our team.",
    followUp: ["How do I contact support?", "What features are available?"]
  }
};

// Find best matching response
const findResponse = (query) => {
  const lowerQuery = query.toLowerCase();

  for (const [keyword, response] of Object.entries(AI_RESPONSES)) {
    if (keyword !== 'default' && lowerQuery.includes(keyword)) {
      return response;
    }
  }

  return AI_RESPONSES.default;
};

const AIHelpAssistant = ({
  mode = 'widget', // 'widget' | 'embedded' | 'fullscreen'
  onClose,
  initialOpen = false
}) => {
  const { profile } = useProfile();
  const [isOpen, setIsOpen] = useState(initialOpen);
  const [messages, setMessages] = useState([
    {
      id: 1,
      type: 'assistant',
      content: `Hi${profile?.firstName ? ` ${profile.firstName}` : ''}! I'm your FamilyForLife assistant. How can I help you today?`,
      timestamp: new Date()
    }
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef(null);
  const inputRef = useRef(null);

  // Scroll to bottom when messages change
  useEffect(() => {
    if (messagesEndRef.current) {
      messagesEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  }, [messages]);

  // Focus input when opened
  useEffect(() => {
    if (isOpen && inputRef.current) {
      setTimeout(() => inputRef.current?.focus(), 100);
    }
  }, [isOpen]);

  const handleSend = async () => {
    if (!inputValue.trim()) return;

    const userMessage = {
      id: Date.now(),
      type: 'user',
      content: inputValue.trim(),
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);

    // Simulate AI thinking delay
    await new Promise(resolve => setTimeout(resolve, 800 + Math.random() * 700));

    // Find matching response
    const response = findResponse(userMessage.content);

    const assistantMessage = {
      id: Date.now() + 1,
      type: 'assistant',
      content: response.answer,
      followUp: response.followUp,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, assistantMessage]);
    setIsTyping(false);
  };

  const handleKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  const handleSuggestedQuestion = (question) => {
    setInputValue(question);
    setTimeout(() => handleSend(), 100);
  };

  const handleFollowUp = (question) => {
    setInputValue(question);
    setTimeout(() => {
      handleSend();
    }, 100);
  };

  const handleNewConversation = () => {
    setMessages([
      {
        id: Date.now(),
        type: 'assistant',
        content: `Hi${profile?.firstName ? ` ${profile.firstName}` : ''}! I'm your FamilyForLife assistant. How can I help you today?`,
        timestamp: new Date()
      }
    ]);
  };

  const handleToggle = () => {
    setIsOpen(!isOpen);
  };

  const handleClose = () => {
    setIsOpen(false);
    if (onClose) onClose();
  };

  // Render message content with markdown-like formatting
  const renderMessageContent = (content) => {
    // Simple markdown: **bold**, bullet points
    const lines = content.split('\n');
    return lines.map((line, idx) => {
      // Handle bold
      const parts = line.split(/(\*\*.*?\*\*)/g);
      const formatted = parts.map((part, i) => {
        if (part.startsWith('**') && part.endsWith('**')) {
          return <strong key={i}>{part.slice(2, -2)}</strong>;
        }
        return part;
      });

      // Handle bullet points
      if (line.trim().startsWith('-') || line.trim().match(/^\d+\./)) {
        return (
          <Typography key={idx} variant="body2" component="li" sx={{ ml: 2 }}>
            {formatted}
          </Typography>
        );
      }

      return (
        <Typography key={idx} variant="body2" sx={{ mb: 0.5 }}>
          {formatted}
        </Typography>
      );
    });
  };

  // Chat content component
  const ChatContent = () => (
    <Box sx={{ display: 'flex', flexDirection: 'column', height: '100%' }}>
      {/* Header */}
      <Box sx={{
        p: 2,
        bgcolor: 'primary.main',
        color: 'primary.contrastText',
        display: 'flex',
        alignItems: 'center',
        gap: 1
      }}>
        <AIIcon />
        <Typography variant="h6" sx={{ flex: 1, fontWeight: 600 }}>
          AI Help Assistant
        </Typography>
        <Tooltip title="New conversation">
          <IconButton size="small" onClick={handleNewConversation} sx={{ color: 'inherit' }}>
            <RefreshIcon fontSize="small" />
          </IconButton>
        </Tooltip>
        {mode === 'widget' && (
          <IconButton size="small" onClick={handleClose} sx={{ color: 'inherit' }}>
            <CloseIcon fontSize="small" />
          </IconButton>
        )}
      </Box>

      {/* Messages */}
      <Box sx={{
        flex: 1,
        overflowY: 'auto',
        p: 2,
        display: 'flex',
        flexDirection: 'column',
        gap: 2
      }}>
        {messages.map((message) => (
          <Box
            key={message.id}
            sx={{
              display: 'flex',
              justifyContent: message.type === 'user' ? 'flex-end' : 'flex-start',
              gap: 1
            }}
          >
            {message.type === 'assistant' && (
              <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
                <AIIcon fontSize="small" />
              </Avatar>
            )}
            <Box sx={{ maxWidth: '80%' }}>
              <Paper
                sx={{
                  p: 1.5,
                  bgcolor: message.type === 'user' ? 'primary.main' : 'background.paper',
                  color: message.type === 'user' ? 'primary.contrastText' : 'text.primary',
                  borderRadius: 2,
                  borderTopLeftRadius: message.type === 'assistant' ? 0 : 2,
                  borderTopRightRadius: message.type === 'user' ? 0 : 2,
                  border: message.type === 'assistant' ? 1 : 0,
                  borderColor: 'divider'
                }}
              >
                {renderMessageContent(message.content)}
              </Paper>

              {/* Follow-up questions */}
              {message.followUp && message.followUp.length > 0 && (
                <Box sx={{ mt: 1, display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                  {message.followUp.map((question, idx) => (
                    <Chip
                      key={idx}
                      label={question}
                      size="small"
                      variant="outlined"
                      onClick={() => handleFollowUp(question)}
                      sx={{ cursor: 'pointer' }}
                    />
                  ))}
                </Box>
              )}
            </Box>
            {message.type === 'user' && (
              <Avatar sx={{ bgcolor: 'secondary.main', width: 32, height: 32 }}>
                {profile?.firstName?.[0] || <PersonIcon fontSize="small" />}
              </Avatar>
            )}
          </Box>
        ))}

        {/* Typing indicator */}
        {isTyping && (
          <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
            <Avatar sx={{ bgcolor: 'primary.main', width: 32, height: 32 }}>
              <AIIcon fontSize="small" />
            </Avatar>
            <Paper sx={{ p: 1.5, bgcolor: 'background.paper', borderRadius: 2, borderTopLeftRadius: 0, border: 1, borderColor: 'divider' }}>
              <Box sx={{ display: 'flex', gap: 0.5 }}>
                <CircularProgress size={8} />
                <CircularProgress size={8} sx={{ animationDelay: '0.2s' }} />
                <CircularProgress size={8} sx={{ animationDelay: '0.4s' }} />
              </Box>
            </Paper>
          </Box>
        )}

        <div ref={messagesEndRef} />
      </Box>

      {/* Suggested questions (show only at start) */}
      {messages.length <= 1 && (
        <Box sx={{ px: 2, pb: 1 }}>
          <Typography variant="caption" color="text.secondary" sx={{ mb: 1, display: 'block' }}>
            Suggested questions:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
            {SUGGESTED_QUESTIONS.slice(0, 4).map((question, idx) => (
              <Chip
                key={idx}
                label={question}
                size="small"
                icon={<QuestionIcon />}
                onClick={() => handleSuggestedQuestion(question)}
                sx={{ cursor: 'pointer' }}
              />
            ))}
          </Box>
        </Box>
      )}

      {/* Input */}
      <Box sx={{ p: 2, borderTop: 1, borderColor: 'divider' }}>
        <TextField
          fullWidth
          size="small"
          placeholder="Ask me anything about FamilyForLife..."
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyPress={handleKeyPress}
          inputRef={inputRef}
          InputProps={{
            endAdornment: (
              <InputAdornment position="end">
                <EmojiPicker
                  onEmojiSelect={(emoji) => setInputValue(prev => prev + emoji)}
                  size="small"
                />
                <IconButton
                  onClick={handleSend}
                  disabled={!inputValue.trim() || isTyping}
                  color="primary"
                  size="small"
                >
                  <SendIcon fontSize="small" />
                </IconButton>
              </InputAdornment>
            )
          }}
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: 0.5, display: 'block', textAlign: 'center' }}>
          Need more help? <Button size="small" href="/help-center">Contact Support</Button>
        </Typography>
      </Box>
    </Box>
  );

  // Widget mode (floating button + popup)
  if (mode === 'widget') {
    return (
      <>
        {/* Floating button */}
        <Zoom in={!isOpen}>
          <Fab
            color="primary"
            sx={{
              position: 'fixed',
              bottom: 24,
              right: 16,
              zIndex: 1000
            }}
            onClick={handleToggle}
          >
            <Tooltip title="AI Help Assistant">
              <AIIcon />
            </Tooltip>
          </Fab>
        </Zoom>

        {/* Chat popup */}
        <Slide direction="up" in={isOpen} mountOnEnter unmountOnExit>
          <Paper
            elevation={8}
            sx={{
              position: 'fixed',
              bottom: 24,
              right: 16,
              width: { xs: 'calc(100% - 32px)', sm: 380 },
              height: { xs: 'calc(100% - 100px)', sm: 520 },
              maxHeight: '80vh',
              borderRadius: 3,
              overflow: 'hidden',
              zIndex: 1001,
              display: 'flex',
              flexDirection: 'column'
            }}
          >
            <ChatContent />
          </Paper>
        </Slide>
      </>
    );
  }

  // Embedded mode (for Help Center integration)
  if (mode === 'embedded') {
    return (
      <Paper sx={{ height: 500, display: 'flex', flexDirection: 'column', overflow: 'hidden' }}>
        <ChatContent />
      </Paper>
    );
  }

  // Fullscreen mode
  return (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      <ChatContent />
    </Box>
  );
};

export default AIHelpAssistant;
